import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Header from 'components/ui/Header';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import { Checkbox } from 'components/ui/Checkbox';
import Icon from 'components/AppIcon';
import Footer from '../landing-page/components/Footer';

const ClientRegistrationPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData?.email?.trim()) {
      newErrors.email = 'L\'email è obbligatoria';
    } else if (!emailRegex?.test(formData?.email)) {
      newErrors.email = 'Inserisci un\'email valida';
    }

    if (!formData?.password) {
      newErrors.password = 'La password è obbligatoria';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'La password deve contenere almeno 8 caratteri';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Conferma la password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Le password non corrispondono';
    }

    if (!formData?.acceptTerms) {
      newErrors.acceptTerms = 'Devi accettare i Termini e Condizioni e la Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            role: 'cliente',
            email_confirmed: false,
            registration_step: 1
          },
          emailRedirectTo: `${import.meta.env?.VITE_SITE_URL || window.location?.origin}/auth/callback?role=cliente`
        }
      });

      if (authError) {
        console.error('❌ Errore di registrazione:', authError);
        
        if (authError?.message?.includes('User already registered')) {
          setErrors({ 
            submit: 'Questo indirizzo email è già registrato. Prova ad accedere o usa un\'altra email.'
          });
        } else if (authError?.message?.includes('fetch') || 
            authError?.message?.includes('Failed to fetch') ||
            authError?.status === 0) {
          setErrors({ 
            submit: 'Errore di connessione. Verifica la tua connessione internet e riprova.'
          });
        } else {
          setErrors({ 
            submit: authError?.message || 'Si è verificato un errore durante la registrazione. Riprova.'
          });
        }
        setIsSubmitting(false);
        return;
      }

      console.log('✅ Registrazione cliente completata:', authData);
      setShowSuccess(true);
    } catch (error) {
      console.error('❌ Errore imprevisto:', error);
      setErrors({
        submit: 'Si è verificato un errore imprevisto. Riprova più tardi.'
      });
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="w-full max-w-md">
            <div className="card text-center">
              <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-success/10 rounded-full">
                <Icon name="CheckCircle" size={40} color="var(--color-success)" strokeWidth={2} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Registrazione Completata!
              </h2>
              <p className="text-muted-foreground mb-6">
                Ti abbiamo inviato un'email di conferma a <strong>{formData?.email}</strong>.
                Clicca sul link nell'email per attivare il tuo account.
              </p>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Nota:</strong> Dopo aver cliccato sul link di conferma, potresti essere reindirizzato a localhost. 
                  Questo è normale. La verifica sarà completata con successo. Torna qui per accedere.
                </p>
              </div>
              <Button
                variant="default"
                fullWidth
                onClick={() => {
                  const params = new URLSearchParams(window.location.search);
                  const redirect = params?.get('redirect');
                  if (redirect && redirect?.startsWith('/')) {
                    navigate(`/login-form?redirect=${encodeURIComponent(redirect)}`);
                  } else {
                    navigate('/login-form');
                  }
                }}
                iconName="ArrowRight"
                iconPosition="right"
              >
                Vai al Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundImage: "url('/assets/images/pic01-1776258054364.png')", backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
    >
      <div className="flex-1 flex flex-col bg-white/70">
        <Header />
        <div className="flex-1 flex items-center justify-center py-40 px-4">
          <div className="w-full max-w-md">
            <div className="card">
              <div className="mb-8 text-center">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-2xl">
                  <Icon name="UserPlus" size={32} color="var(--color-primary)" strokeWidth={2.5} />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                  Registrati come Cliente
                </h1>
                <p className="text-muted-foreground caption">
                  Trova i migliori professionisti per i tuoi progetti
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <Input
                  label="Email"
                  type="email"
                  name="email"
                  placeholder="tuo@email.it"
                  value={formData?.email}
                  onChange={handleChange}
                  error={errors?.email}
                  required
                  disabled={isSubmitting}
                />

                <div className="relative">
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Minimo 8 caratteri"
                    value={formData?.password}
                    onChange={handleChange}
                    error={errors?.password}
                    required
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[42px] text-muted-foreground hover:text-foreground transition-colors duration-250"
                    disabled={isSubmitting}
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    <Icon 
                      name={showPassword ? 'EyeOff' : 'Eye'} 
                      size={20} 
                      strokeWidth={2}
                    />
                  </button>
                </div>

                <div className="relative">
                  <Input
                    label="Conferma Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    placeholder="Ripeti la password"
                    value={formData?.confirmPassword}
                    onChange={handleChange}
                    error={errors?.confirmPassword}
                    required
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-[42px] text-muted-foreground hover:text-foreground transition-colors duration-250"
                    disabled={isSubmitting}
                    aria-label={showConfirmPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    <Icon 
                      name={showConfirmPassword ? 'EyeOff' : 'Eye'} 
                      size={20} 
                      strokeWidth={2}
                    />
                  </button>
                </div>

                <Checkbox
                  label={
                    <span className="text-sm md:text-base">
                      Accetto i{' '}
                      <Link to="/terms-and-conditions" className="text-primary hover:underline font-medium">
                        Termini e Condizioni
                      </Link>
                      {' '}e la{' '}
                      <Link to="/privacy-policy" className="text-primary hover:underline font-medium">
                        Privacy Policy
                      </Link>
                    </span>
                  }
                  name="acceptTerms"
                  checked={formData?.acceptTerms}
                  onChange={handleChange}
                  error={errors?.acceptTerms}
                  required
                  disabled={isSubmitting}
                />

                {errors?.submit && (
                  <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Icon name="AlertCircle" size={20} color="var(--color-error)" strokeWidth={2} />
                      <p className="text-sm text-error whitespace-pre-line flex-1">
                        {errors?.submit}
                      </p>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  loading={isSubmitting}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Registrati come Cliente
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t border-border">
                <p className="text-center text-muted-foreground caption mb-4">
                  Hai già un account?{' '}
                  <Link
                    to="/login-form"
                    className="text-primary hover:underline font-medium"
                  >
                    Accedi
                  </Link>
                </p>
                <p className="text-center text-muted-foreground caption">
                  Sei un professionista?{' '}
                  <Link
                    to="/signup-form"
                    className="text-primary hover:underline font-medium"
                  >
                    Registrati come Professionista
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default ClientRegistrationPage;